import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { commonMessages } from '@/i18n/messages/common'
import { usersMessages } from '@/i18n/messages/users'
import { useWfT } from '@/features/workflow/shared'
import { Select } from '@/components/Select'
import { Field } from './EditUserDialog'
import { Credentials, ErrorLine, Modal, btnPrimary, btnSecondary, inputCls, labelCls } from './parts'
import { loginTypeLabel, roleName, suggestUsername, type AccessRole, type AuOptions, type EmployeeHit } from './types'

/**
 * A new AU login with its Aufmass access in one step. The password is generated on the server and
 * shown here once; it works in AU and in Aufmass.
 */
export function CreateAccountDialog({ au, roles: catalog, onClose }: { au: AuOptions; roles: AccessRole[]; onClose: () => void }) {
  const qc = useQueryClient()
  const t = useT(usersMessages)
  const tw = useWfT()
  const tc = useT(commonMessages)
  const [form, setForm] = useState({ name: '', surname: '', username: '', email: '', loginType: au.loginTypes.includes(1) ? 1 : au.loginTypes[0] ?? 1, companyId: '' })
  const [usernameTouched, setUsernameTouched] = useState(false)
  const [employee, setEmployee] = useState<EmployeeHit | null>(null)
  const [q, setQ] = useState('')
  const [roles, setRoles] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ username: string; password: string } | null>(null)

  const setName = (patch: Partial<typeof form>) => setForm((f) => {
    const next = { ...f, ...patch }
    return usernameTouched ? next : { ...next, username: suggestUsername(next.name, next.surname) }
  })

  const hits = useQuery<EmployeeHit[]>({
    queryKey: ['au-employees', q],
    queryFn: () => api.get<EmployeeHit[]>(`/api/users/employees?q=${encodeURIComponent(q)}`),
    enabled: q.trim().length >= 2 && !employee,
  })

  const create = useMutation({
    mutationFn: () => api.post<{ userId: string; username: string; password: string }>('/api/users/au-accounts', {
      name: form.name, surname: form.surname, username: form.username, email: form.email || null,
      loginType: form.loginType, companyId: form.companyId || null, employeeId: employee?.id ?? null,
      roles: [...roles], pages: [],
    }),
    onSuccess: (r) => { setCreated({ username: r.username, password: r.password }); void qc.invalidateQueries({ queryKey: ['users'] }) },
    onError: (e) => setError(e instanceof ApiError ? e.message : t('couldNotSave')),
  })

  if (created)
    return (
      <Modal onClose={onClose} title={t('credentialsTitle')}
        footer={<><span /><button onClick={onClose} className={btnPrimary}>{t('done')}</button></>}>
        <Credentials username={created.username} password={created.password} />
      </Modal>
    )

  const ready = form.name.trim() && form.surname.trim() && form.username.trim().length >= 3
  return (
    <Modal onClose={onClose} wide title={t('createTitle')} subtitle={t('createHint')}
      footer={<>
        <ErrorLine error={error} />
        <div className="flex gap-2">
          <button onClick={onClose} className={btnSecondary}>{tc('cancel')}</button>
          <button onClick={() => { setError(null); create.mutate() }} disabled={!ready || create.isPending} className={btnPrimary}>
            {create.isPending ? t('creating') : t('create')}
          </button>
        </div>
      </>}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label={t('firstName')}><input className={inputCls} value={form.name} autoFocus onChange={(e) => setName({ name: e.target.value })} /></Field>
        <Field label={t('lastName')}><input className={inputCls} value={form.surname} onChange={(e) => setName({ surname: e.target.value })} /></Field>
        <Field label={t('username')} hint={t('usernameHint')}>
          <input className={`${inputCls} font-mono`} value={form.username} autoCapitalize="none" spellCheck={false}
                 onChange={(e) => { setUsernameTouched(true); setForm({ ...form, username: e.target.value.trim() }) }} />
        </Field>
        <Field label={t('email')}><input className={inputCls} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label={t('auType')}>
          <Select value={String(form.loginType)} onChange={(v) => setForm({ ...form, loginType: Number(v) })}
            options={au.loginTypes.map((lt) => ({ value: String(lt), label: loginTypeLabel(lt) }))} />
        </Field>
        <Field label={t('company')}>
          <Select value={form.companyId} onChange={(v) => setForm({ ...form, companyId: v })}
            empty={{ value: '', label: t('none') }}
            options={au.companies.map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
      </div>

      <div className="space-y-1">
        <span className={labelCls}>{t('employee')}</span>
        {employee ? (
          <div className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-3 py-2 text-sm dark:bg-neutral-800/50">
            <span>{employee.name}{employee.company ? <span className="text-neutral-500"> · {employee.company}</span> : null}</span>
            <button type="button" onClick={() => { setEmployee(null); setQ('') }} className="text-xs text-neutral-500 hover:underline">{tc('close')}</button>
          </div>
        ) : (
          <>
            <input className={inputCls} value={q} placeholder={t('employeeSearch')} onChange={(e) => setQ(e.target.value)} />
            {(hits.data ?? []).length > 0 && (
              <ul className="max-h-40 overflow-y-auto rounded-lg ring-1 ring-neutral-200 dark:ring-neutral-700">
                {hits.data!.map((h) => (
                  <li key={h.id}>
                    <button type="button" disabled={h.hasLogin} onClick={() => {
                      setEmployee(h)
                      if (!form.name && !form.surname) { const [first, ...rest] = h.name.split(' '); setName({ name: first ?? '', surname: rest.join(' ') }) }
                    }}
                      className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-neutral-800">
                      <span>{h.name}{h.company ? <span className="text-neutral-500"> · {h.company}</span> : null}</span>
                      {h.hasLogin && <span className="text-xs text-neutral-500">{t('hasLogin')}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>

      <div>
        <span className={labelCls}>{t('rolesTitle')}</span>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {catalog.map((r) => (
            <button key={r.role} type="button" aria-pressed={roles.has(r.role)}
              onClick={() => setRoles((prev) => { const n = new Set(prev); if (n.has(r.role)) n.delete(r.role); else n.add(r.role); return n })}
              className={`rounded-full px-2.5 py-1 text-xs font-medium ring-1 ${roles.has(r.role) ? 'bg-brand-600 text-white ring-brand-600' : 'text-neutral-700 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-200 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
              {roleName(tw, r.role, catalog)}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  )
}
